// src/state/profileStore.js
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Safe merge that preserves existing values when incoming values are null/undefined
 */
function safeMerge(base, incoming) {
  if (incoming == null) return base ?? null;
  if (typeof incoming !== 'object' || Array.isArray(incoming)) return incoming;

  const out = { ...(base && typeof base === 'object' ? base : {}) };
  for (const key of Object.keys(incoming)) {
    const nextVal = incoming[key];
    if (nextVal === undefined || nextVal === null) continue;
    out[key] = safeMerge(out[key], nextVal);
  }
  return out;
}

/**
 * Ensure consistent profile structure with both top-level and nested business fields
 */
function normalizeProfile(p) {
  if (!p || typeof p !== 'object') return p ?? null;
  
  const profile = { ...p };
  profile.business = profile.business ? { ...profile.business } : {};
  
  // Mirror business fields to top level for backward compatibility
  const businessFields = [
    'b_name', 'b_business_industry', 'b_phone', 'b_website', 'b_email',
    'b_address', 'b_city', 'b_state', 'b_zip_code', 'b_country',
    'b_customer_focus', 'b_show_address', 'b_logo', 'b_about_us',
    'b_lat', 'b_lng', 'b_place_id', 'b_service_distance'
  ];
  
  businessFields.forEach(field => {
    if (profile.business[field] !== undefined && profile.business[field] !== null) {
      profile[field] = profile.business[field];
    } else if (profile[field] !== undefined && profile[field] !== null) {
      profile.business[field] = profile[field];
    }
  });
  
  return profile;
}

export const useProfileStore = create(
  persist(
    (set, get) => ({
      // State
      profile: null,
      lastServerSyncAt: null,
      isLoading: false,
      error: null,
      ready: false,

      // Actions
      login: async (email, password) => {
        console.log('Login function called:', { email, password: '****' }); // Debug log
        set({ isLoading: true, error: null });
        try {
          // Use relative URL so the request goes through the CRA proxy in dev.
          // This ensures the httpOnly cookie is set on the correct origin (port 3000).
          const apiUrl = '/api/auth/login';
          console.log('Attempting fetch to:', apiUrl); // Debug log
          console.log('Environment API URL:', process.env.REACT_APP_API_URL); // Debug log
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
            credentials: 'include',
          });
          console.log('Fetch response status:', response.status); // Debug log
          const data = await response.json();
          console.log('Fetch response data:', data); // Debug log

          // 2FA required — password was correct but user has 2FA enabled
          if (data.requires2FA) {
            set({ isLoading: false });
            return {
              success: false,
              requires2FA: true,
              pendingToken: data.pendingToken,
              maskedEmail: data.maskedEmail,
            };
          }

          // Account exists but the user never set a password (e.g. an OAuth
          // signup that didn't finish /create-password). The server has emailed
          // a set-password link; surface the friendly message, don't throw.
          if (data.needsPasswordSetup) {
            set({ isLoading: false });
            return {
              success: false,
              needsPasswordSetup: true,
              error: data.message || 'Check your email to set your password and finish signing in.',
            };
          }

          if (!response.ok || !data?.user) {
            console.error('Login failed:', data?.error || 'No user data in response'); // Debug log
            throw new Error(data?.error || 'Login failed: No user data');
          }
          // Complete login (shared with verify2FA)
          return get()._completeLogin(data);
        } catch (error) {
          console.error('Login fetch error:', error.message); // Debug log
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      // Shared post-login logic used by both login() and verify2FA()
      _completeLogin: async (data) => {
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        const userData = {
          ...data.user,
          business: data.user?.business || {},
          isAuthenticated: true,
          u_is_new_user: data.user?.u_is_new_user ?? false,
        };
        const normalized = normalizeProfile(userData);
        console.log('Normalized profile:', normalized);
        set({
          profile: normalized,
          isLoading: false,
          error: null,
          lastServerSyncAt: Date.now(),
        });
        // Start Matrix service in background
        try {
          const { matrixUserService } = await import('../components/messaging/matrixUserService');
          matrixUserService.startForUser(normalized.u_id)
            .then(() => console.log('Matrix service started successfully'))
            .catch((err) => console.warn('Matrix service start failed (non-blocking):', err));
        } catch (err) {
          console.warn('Matrix service import failed:', err);
        }
        return { success: true, user: normalized };
      },

      // Complete login after 2FA code verification
      verify2FA: async (pendingToken, code) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/auth/2fa/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pendingToken, code }),
            credentials: 'include',
          });
          const data = await response.json();
          if (!response.ok || !data?.user) {
            throw new Error(data?.error || 'Verification failed');
          }
          return get()._completeLogin(data);
        } catch (error) {
          set({ isLoading: false, error: error.message });
          return { success: false, error: error.message };
        }
      },

      setProfile: (profileData) => {
        const normalized = normalizeProfile(profileData);
        set({ 
          profile: normalized,
          error: null,
          lastServerSyncAt: Date.now()
        });
        return normalized;
      },

      updateProfile: (updates) => {
        const currentProfile = get().profile;
        if (!currentProfile) return;
        
        const updatedProfile = safeMerge(currentProfile, updates);
        const normalized = normalizeProfile(updatedProfile);
        
        set({ profile: normalized });
        return normalized;
      },

      refreshProfile: async (userId) => {
        if (!userId) return null;
        
        set({ isLoading: true, error: null });
        
        try {
          const response = await fetch(
            `/api/profile/${userId}`,
            { credentials: 'include' }
          );
          
          if (!response.ok) {
            throw new Error(`Failed to fetch profile: ${response.status}`);
          }
          
          const profileData = await response.json();

          // Pull ratings the exact same way userprofile.jsx does
          let ratingPatch = {};
          try {
            const ratingRes = await fetch(
              `/api/profile/${userId}/rating-summary`,
              { credentials: 'include' }
            );

            if (ratingRes.ok) {
              const ratingData = await ratingRes.json();
              console.log('[profileStore.refreshProfile] rating-summary:', ratingData);
              const countRaw =
                ratingData?.total_reviews ??
                ratingData?.totalReviews ??
                ratingData?.total_review_count ??
                ratingData?.totalReviewCount ??
                ratingData?.review_count ??
                ratingData?.reviewCount ??
                ratingData?.u_review_count ??
                ratingData?.uReviewCount ??
                ratingData?.count ??
                ratingData?.reviews ??
                0;

              const count = Number.isFinite(Number(countRaw)) ? Number(countRaw) : 0;


              const avgRaw =
                ratingData?.u_avg_rating ??
                ratingData?.avg_rating ??
                null;

              const avgParsed = avgRaw != null ? parseFloat(avgRaw) : null;
              const avg = count > 0 && Number.isFinite(avgParsed) ? avgParsed : null;

              const totalRaw = ratingData?.u_total_review_score ?? null;
              const totalParsed = totalRaw != null ? parseFloat(totalRaw) : null;
              const total = Number.isFinite(totalParsed) ? totalParsed : 0;

              ratingPatch = {
                u_review_count: count,
                u_avg_rating: avg,
                u_total_review_score: total,
              };
            }
          } catch (err) {
            console.warn('Profile rating-summary fetch failed:', err);
          }

          const normalized = normalizeProfile({
            ...profileData,
            ...ratingPatch,
          });

          set({ 
            profile: normalized,
            isLoading: false,
            error: null,
            lastServerSyncAt: Date.now()
          });

          return normalized;

        } catch (error) {
          console.error('Profile refresh error:', error);
          set({ 
            isLoading: false, 
            error: error.message,
            lastServerSyncAt: get().lastServerSyncAt // Keep old timestamp
          });
          return get().profile; // Return existing profile on error
        }
      },

      clearProfile: () => {
        set({
          profile: null,
          lastServerSyncAt: null,
          isLoading: false,
          error: null,
        });
      },

      logout: async () => {
        try {
          await fetch('/api/auth/logout', {
            method: 'POST',
            credentials: 'include',
          });
        } catch (err) {
          console.warn('logout request failed', err);
        } finally {
          set({
            profile: null,
            lastServerSyncAt: null,
            isLoading: false,
            error: null,
          });
        }
      },


      // Simple selectors that don't cause re-renders
      getServiceDistance: () => {
        const profile = get().profile;
        return profile?.b_service_distance ?? profile?.business?.b_service_distance ?? 25;
      },

      getLatLng: () => {
        const profile = get().profile;
        const lat = profile?.b_lat ?? profile?.business?.b_lat;
        const lng = profile?.b_lng ?? profile?.business?.b_lng;
        
        if (lat !== undefined && lng !== undefined) {
          return { lat: parseFloat(lat), lng: parseFloat(lng) };
        }
        return null;
      },

      getCustomerFocus: () => {
        const profile = get().profile;
        return profile?.u_customer_focus ?? profile?.business?.b_customer_focus ?? null;
      }
    }),
    {
      name: 'eh_profile_v1',
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        profile: state.profile,
        lastServerSyncAt: state.lastServerSyncAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) state.ready = true;
        // On page refresh, restart Matrix client if user was logged in
        if (state?.profile?.u_id) {
          import('../components/messaging/matrixUserService').then(({ matrixUserService }) => {
            if (!matrixUserService.isRunning) {
              console.log('[ProfileStore] Rehydrated — restarting Matrix for user:', state.profile.u_id);
              matrixUserService.startForUser(state.profile.u_id);
            }
          });
        }
      },
    }
  )
);

// Convenience hook
export const useProfile = () => useProfileStore(state => state.profile);